import { Drawer } from 'theme';

// Both responsive controls can touch the same active chips. Keep their native
// state shared so reconnecting either control never captures a grouped label.
const activeFacetOriginals = new WeakMap();

const RaithFilterControls = Base => class extends Base {
  connectedCallback() {
    super.connectedCallback?.();
    this.filterController?.abort();
    this.filterController = new AbortController();
    this.form = this.querySelector('form');
    this.applyButton = this.querySelector('[data-filter-apply]');
    this.status = this.querySelector('[data-filter-status]');
    if (!this.form || !this.applyButton) return;
    this.buildGroups();
    this.groupActiveFacets();
    const { signal } = this.filterController;
    this.addEventListener('dialog:before-show', () => this.restore(), { signal });
    this.addEventListener('dialog:before-hide', () => this.restore(), { signal });
    const changed = event => {
      const group = this.filterGroups.find(group => group.proxy === event.target);
      if (group) {
        for (const input of group.inputs) input.checked = group.proxy.checked;
        if (group.subcategories) group.selected = new Set(group.proxy.checked ? group.values.keys() : []);
      } else {
        const childGroup = this.filterGroups.find(group => group.subcategories && group.inputs.includes(event.target));
        if (childGroup) {
          const value = event.target.value.trim().toLowerCase();
          if (event.target.checked) childGroup.selected.add(value);
          else childGroup.selected.delete(value);
        }
      }
      this.syncGroups();
      this.queueCount();
    };
    this.form.addEventListener('input', changed, { signal });
    this.form.addEventListener('change', changed, { signal });
    this.form.addEventListener('submit', event => this.apply(event), { signal });
    this.querySelector('[data-filter-clear]').addEventListener('click', () => {
      for (const input of this.form.querySelectorAll('input[name^="filter."]')) {
        if (input.type === 'checkbox') input.checked = false;
        else input.value = '';
      }
      for (const group of this.filterGroups) if (group.subcategories) group.selected.clear();
      this.syncGroups();
      this.queueCount();
    }, { signal });
  }

  buildGroups() {
    this.form.querySelectorAll('[data-filter-subcategory-group]').forEach(group => {
      for (const choice of group.querySelectorAll('[data-filter-group-source]')) group.before(choice);
      group.remove();
    });
    this.form.querySelectorAll('[data-filter-group-proxy]').forEach(choice => choice.remove());
    this.form.querySelectorAll('[data-filter-group-source]').forEach(choice => {
      choice.hidden = false;
      choice.removeAttribute('data-filter-group-source');
    });
    this.filterGroups = [];
    this.categoryGroups = new Map();
    for (const fieldset of this.form.querySelectorAll('fieldset[data-filter-operator="OR"]')) {
      const groups = new Map();
      for (const choice of fieldset.querySelectorAll('.raith-filter__choice[data-filter-group]')) {
        if (choice.closest('fieldset') !== fieldset) continue;
        const input = choice.querySelector('input[type="checkbox"][name^="filter."]');
        const name = choice.dataset.filterGroup?.trim();
        if (!input || !name || input.disabled) continue;
        const key = JSON.stringify([input.name, name]);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({ choice, input });
      }
      for (const entries of groups.values()) {
        const values = new Map();
        for (const entry of entries) for (const value of (entry.choice.dataset.filterGroupValues || '').split('|')) {
          if (value.trim()) values.set(value.trim().toLowerCase(), value.trim());
        }
        for (const entry of entries) values.set(entry.input.value.trim().toLowerCase(), entry.input.value);
        const subcategories = this.dataset.showFilterSubcategories !== 'false' && fieldset.dataset.filterKind === 'category' && values.size > 1;
        if (entries.length < 2 && !subcategories) continue;
        const choice = entries[0].choice.cloneNode(true);
        const proxy = choice.querySelector('input');
        const label = choice.querySelector('label');
        const labelText = label?.querySelector('.raith-filter__label');
        if (!proxy || !labelText) continue;
        choice.removeAttribute('data-filter-group');
        choice.setAttribute('data-filter-group-proxy', '');
        proxy.removeAttribute('name');
        proxy.removeAttribute('value');
        proxy.removeAttribute('checked');
        proxy.id = `raith-filter-group-${this.id || this.dataset.sectionId}-${this.filterGroups.length}`;
        label.htmlFor = proxy.id;
        labelText.textContent = entries[0].choice.dataset.filterGroup;
        choice.querySelectorAll('.raith-filter__count').forEach(count => count.remove());
        entries[0].choice.before(choice);
        for (const entry of entries) {
          entry.choice.hidden = !subcategories;
          entry.choice.setAttribute('data-filter-group-source', '');
        }
        const group = { proxy, inputs: entries.map(entry => entry.input), subcategories, values };
        if (subcategories) {
          const activeValues = this.activeCategoryValues(fieldset, entries[0].input.name);
          group.defaultSelected = new Set([...values.keys()].filter(value => activeValues.has(value)));
          group.selected = new Set(group.defaultSelected);
          group.defaultFull = [...values.keys()].every(value => group.defaultSelected.has(value));
          this.categoryGroups.set(JSON.stringify([entries[0].input.name, entries[0].choice.dataset.filterGroup.trim()]), group);
          const wrapper = document.createElement('div'), row = document.createElement('div'), children = document.createElement('div'), toggle = document.createElement('button');
          wrapper.className = 'raith-filter__subcategory-group';
          wrapper.setAttribute('data-filter-subcategory-group', '');
          row.className = 'raith-filter__subcategory-heading';
          children.className = 'raith-filter__subcategories';
          children.id = `${proxy.id}-children`;
          toggle.type = 'button';
          toggle.className = 'raith-filter__subcategory-toggle';
          toggle.setAttribute('aria-controls', children.id);
          toggle.setAttribute('aria-label', entries[0].choice.dataset.filterGroup);
          toggle.innerHTML = '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="m6 9 6 6 6-6"/></svg>';
          const setOpen = open => { children.hidden = !open; toggle.setAttribute('aria-expanded', String(open)); };
          setOpen(this.dataset.openFilterSubcategories === 'true' || (group.selected.size > 0 && !group.defaultFull));
          toggle.addEventListener('click', () => setOpen(children.hidden), { signal: this.filterController.signal });
          choice.before(wrapper);
          row.append(choice, toggle);
          children.append(...entries.map(entry => entry.choice));
          wrapper.append(row, children);
        }
        this.filterGroups.push(group);
      }
    }
    this.syncGroups();
  }

  activeCategoryValues(fieldset, name) {
    let values = [];
    try { values = JSON.parse(fieldset.dataset.filterActiveValues || '[]'); } catch (_) {}
    const selected = new Set(Array.isArray(values) ? values.map(value => String(value).trim().toLowerCase()) : []);
    for (const input of fieldset.querySelectorAll('input[name]')) if (input.name === name && input.defaultChecked) selected.add(input.value.trim().toLowerCase());
    if (fieldset.dataset.filterActiveUrl && fieldset.dataset.filterFirstActiveValue) {
      try {
        const url = new URL(fieldset.dataset.filterActiveUrl, window.location.href);
        for (const value of url.searchParams.getAll(name)) selected.add(value.trim().toLowerCase());
        selected.add(fieldset.dataset.filterFirstActiveValue.trim().toLowerCase());
      } catch (_) {}
    }
    return selected;
  }

  syncGroups() {
    for (const group of this.filterGroups || []) {
      const { proxy, inputs } = group;
      const selected = group.subcategories ? group.selected.size : inputs.filter(input => input.checked).length;
      const total = group.subcategories ? group.values.size : inputs.length;
      proxy.checked = selected === total;
      proxy.indeterminate = selected > 0 && selected < total;
    }
  }

  groupActiveFacets() {
    const section = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
    if (!section) return;
    const aliases = new Map();
    const groups = new Map();
    for (const choice of this.form.querySelectorAll('fieldset[data-filter-operator="OR"] .raith-filter__choice[data-filter-group]')) {
      if (choice.closest('fieldset')?.dataset.filterOperator !== 'OR') continue;
      const input = choice.querySelector('input[type="checkbox"][name^="filter."]');
      const alias = choice.dataset.filterGroup?.trim();
      if (!input || !alias) continue;
      const key = JSON.stringify([input.name, alias]);
      if (this.categoryGroups.get(key)?.defaultFull === false) continue;
      const label = choice.closest('fieldset').dataset.filterLabel;
      const prefix = this.dataset.showFilterGroupName === 'true' && label ? `${label}: ` : '';
      if (!groups.has(key)) groups.set(key, { name: input.name, alias, prefix, values: new Set(), chips: [] });
      groups.get(key).values.add(input.value);
      for (const value of (choice.dataset.filterGroupValues || '').split('|')) {
        if (value.trim()) groups.get(key).values.add(value.trim());
      }
      for (const value of groups.get(key).values) aliases.set(JSON.stringify([input.name, value.trim().toLowerCase()]), key);
    }
    for (const chip of section.querySelectorAll('.removable-facet[data-raith-filter-param][data-raith-filter-value]')) {
      const label = chip.querySelector('span.text-subdued');
      const link = chip.querySelector('facet-link > a');
      if (!label || !link) continue;
      if (!activeFacetOriginals.has(chip)) activeFacetOriginals.set(chip, { text: label.textContent, href: link.getAttribute('href'), aria: link.getAttribute('aria-label'), hidden: chip.hidden });
      const original = activeFacetOriginals.get(chip);
      label.textContent = original.text;
      link.setAttribute('href', original.href);
      if (original.aria === null) link.removeAttribute('aria-label');
      else link.setAttribute('aria-label', original.aria);
      chip.hidden = original.hidden;
      const key = aliases.get(JSON.stringify([chip.dataset.raithFilterParam, chip.dataset.raithFilterValue.trim().toLowerCase()]));
      if (key) {
        const group = groups.get(key);
        group.values.add(chip.dataset.raithFilterValue);
        group.chips.push({ chip, label, link, original });
      }
    }
    for (const group of groups.values()) {
      if (!group.chips.length) continue;
      const first = group.chips[0];
      first.label.textContent = group.prefix + group.alias;
      if (first.original.aria) first.link.setAttribute('aria-label', first.original.aria.replace(first.chip.dataset.raithFilterValue, group.alias));
      // The new controls connect before the facet handler updates history.
      // Start with this response's native removal URL, not the previous page.
      const url = new URL(first.original.href, window.location.href);
      const normalize = value => value.trim().toLowerCase();
      const knownValues = new Set([...group.values].map(normalize));
      const remaining = url.searchParams.getAll(group.name).filter(value => {
        if (knownValues.has(normalize(value))) return false;
        if (group.name.includes('.option.size')) {
          const parts = value.split(' - ');
          const knownLength = parts.length === 1 || (parts.length === 2 && ['reg', 'regular', 'long', 'extra long'].includes(normalize(parts[1])));
          if (knownLength && normalize(parts[0]) === normalize(group.alias)) return false;
        }
        return true;
      });
      url.searchParams.delete(group.name);
      for (const value of remaining) url.searchParams.append(group.name, value);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      first.link.href = url.toString();
      for (const { chip } of group.chips.slice(1)) chip.hidden = true;
    }
    const visibleCount = [...section.querySelectorAll('.active-facets .removable-facet')].filter(chip => !chip.hidden).length;
    for (const button of section.querySelectorAll('[data-raith-filter-toggle][data-filter-label]')) {
      button.textContent = `${button.dataset.filterLabel}${visibleCount ? ` (${visibleCount})` : ''}`;
    }
  }

  disconnectedCallback() {
    this.invalidateCount();
    this.filterController?.abort();
    super.disconnectedCallback?.();
  }

  invalidateCount() {
    clearTimeout(this.countTimer);
    this.countController?.abort();
    this.countVersion = (this.countVersion || 0) + 1;
  }

  restore() {
    this.invalidateCount();
    this.form.reset();
    for (const group of this.filterGroups || []) if (group.subcategories) group.selected = new Set(group.defaultSelected);
    this.syncGroups();
    this.showCount(this.dataset.productsCount, false);
  }

  buildUrl() {
    const url = new URL(window.location.href);
    url.pathname = new URL(this.form.action).pathname;
    url.hash = '';
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.') || key === 'page' || key === 'section_id') url.searchParams.delete(key);
    }
    const fields = new FormData(this.form);
    for (const key of new Set(fields.keys())) url.searchParams.delete(key);
    for (const [key, value] of fields) {
      if (value !== '') url.searchParams.append(key, value);
    }
    for (const group of this.filterGroups || []) {
      if (!group.subcategories) continue;
      const name = group.inputs[0].name, present = new Set(url.searchParams.getAll(name).map(value => value.trim().toLowerCase()));
      for (const selected of group.selected) if (!present.has(selected)) url.searchParams.append(name, group.values.get(selected));
    }
    // A filtered response may omit zero-count members of a selected OR group.
    // Keep its explicitly configured membership when all rendered members are selected.
    for (const fieldset of this.form.querySelectorAll('fieldset[data-filter-operator="OR"]')) {
      if (this.dataset.showFilterSubcategories !== 'false' && fieldset.dataset.filterKind === 'category') continue;
      const groups = new Map();
      for (const choice of fieldset.querySelectorAll('.raith-filter__choice[data-filter-group]')) {
        if (choice.closest('fieldset') !== fieldset) continue;
        const input = choice.querySelector('input[type="checkbox"][name^="filter."]');
        const alias = choice.dataset.filterGroup?.trim();
        if (!input || input.disabled || !alias || input.name.includes('.option.size')) continue;
        const key = JSON.stringify([input.name, alias]);
        if (!groups.has(key)) groups.set(key, { name: input.name, inputs: [], values: [] });
        const group = groups.get(key);
        group.inputs.push(input);
        group.values.push(...(choice.dataset.filterGroupValues || '').split('|').map(value => value.trim()).filter(Boolean));
      }
      for (const group of groups.values()) {
        if (!group.values.length || !group.inputs.every(input => input.checked)) continue;
        const nativeValues = new Map(group.inputs.map(input => [input.value.toLowerCase(), input.value]));
        const existing = new Set(url.searchParams.getAll(group.name).map(value => value.toLowerCase()));
        for (const configured of group.values) {
          const key = configured.toLowerCase();
          if (existing.has(key)) continue;
          url.searchParams.append(group.name, nativeValues.get(key) || configured);
          existing.add(key);
        }
      }
    }
    url.searchParams.set('section_id', this.dataset.sectionId);
    return url;
  }

  showCount(value, announce = true) {
    const count = Number(value);
    if (value === null || value === '' || !Number.isSafeInteger(count) || count < 0) throw new Error('Missing native result count');
    this.applyButton.textContent = count === 1 ? this.dataset.showOne : this.dataset.showCount.replace('__COUNT__', String(count));
    this.applyButton.removeAttribute('aria-busy');
    this.status.textContent = announce ? (count === 1 ? this.dataset.statusOne : this.dataset.status.replace('__COUNT__', String(count))) : '';
  }

  queueCount() {
    this.invalidateCount();
    this.applyButton.textContent = this.dataset.apply;
    this.applyButton.setAttribute('aria-busy', 'true');
    this.status.textContent = this.dataset.loading;
    const version = this.countVersion;
    this.countTimer = setTimeout(() => this.fetchCount(version), 250);
  }

  async fetchCount(version) {
    this.countController = new AbortController();
    try {
      const url = this.buildUrl();
      url.searchParams.set('section_id', 'raith-collection-count');
      const response = await fetch(url, { signal: this.countController.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Filter count unavailable');
      const text = await response.text();
      if (text.length > 8192) throw new Error('Unexpected count response');
      const html = new DOMParser().parseFromString(text, 'text/html');
      const markers = html.querySelectorAll('[data-raith-collection-count]');
      const count = markers[0];
      if (markers.length !== 1 || ['collectionHandle', 'country', 'currency', 'locale'].some(key => !this.dataset[key] || count.dataset[key] !== this.dataset[key])) throw new Error('Count context changed');
      if (version !== this.countVersion || !this.isConnected || (this.localName === 'raith-filter-drawer' && !this.open)) return;
      this.showCount(count.getAttribute('data-raith-collection-count'));
    } catch (error) {
      if (version !== this.countVersion || error.name === 'AbortError') return;
      this.applyButton.textContent = this.dataset.apply;
      this.applyButton.removeAttribute('aria-busy');
      this.status.textContent = '';
    }
  }

  async apply(event) {
    event.preventDefault();
    if (this.applying) return;
    this.applying = true;
    const url = this.buildUrl();
    this.invalidateCount();
    await this.hide?.();
    this.applying = false;
    if (!this.isConnected) return;
    this.dispatchEvent(new CustomEvent('facet:update', { bubbles: true, detail: { url } }));
  }
};

export class RaithFilterDrawer extends RaithFilterControls(Drawer) {}

if (!customElements.get('raith-filter-drawer')) customElements.define('raith-filter-drawer', RaithFilterDrawer);
if (!customElements.get('raith-filter-sidebar')) customElements.define('raith-filter-sidebar', class extends RaithFilterControls(HTMLElement) {});
